import React, { useState, useEffect, useRef } from 'react';
import {
  CircularProgress,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  ThemeProvider,
  styled,
} from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import {
  useHistory, useLocation, useRouteMatch, Redirect,
} from 'react-router-dom';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';

import { ContentContainer } from './ActivationSteps.styles';

import { ORGANIZATIONS_BY_PREFIX_QUERY } from '@/apollo/queries';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { getTheme, type Product } from '@/utils/productHelpers';
import { buildRedirectParam } from '@/utils/urlHelpers';
import { logError } from '@/utils/logError';
import { SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';
import { ActiveFeatureFlag, isBooleanFeatureFlagEnabled } from '@/hooks/usePostHogFeatureFlags';
import { useConsent } from '@/hooks/useConsent';

const DROPDOWN_RESULTS_LIMIT = 3;
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const SLEEPIO_CANOPY_LOB = 'wellness';

interface Organization {
  name?: string | null;
  slug: string;
}

const SuggestionsContainer = styled(Paper)({
  position: 'absolute',
  zIndex: 1000,
  width: '100%',
  maxHeight: 200,
  overflow: 'auto',
  marginTop: 4,
});

const InputContainer = styled(Box)({
  position: 'relative',
  marginBottom: 32,
});

interface SuggestionContentProps {
  loading: boolean;
  error: boolean;
  organizations: Organization[];
  onSelect: (org: Organization) => void;
}

const SuggestionContent: React.FC<SuggestionContentProps> = ({
  loading, error, organizations, onSelect,
}) => {
  if (error) {
    return (
      <ListItem>
        <ListItemText primary="Something went wrong. Clear the field and try again." />
      </ListItem>
    );
  }
  if (loading) {
    return (
      <ListItem>
        <ListItemText primary="Searching…" />
      </ListItem>
    );
  }
  if (organizations.length > 0) {
    return (
      <List>
        {organizations.map((org) => (
          <ListItem
            key={org.slug}
            button
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(org);
            }}
          >
            <ListItemText primary={org.name ?? org.slug} />
          </ListItem>
        ))}
      </List>
    );
  }
  return (
    <ListItem>
      <ListItemText primary="No employers found. Try a different search term." />
    </ListItem>
  );
};

const EmployerSelection: React.FC = () => {
  const isSupabaseAuthEnabled = isBooleanFeatureFlagEnabled(
    useFeatureFlagVariantKey(ActiveFeatureFlag.CAP_USE_SUPABASE_AUTH),
  );
  const history = useHistory();
  const location = useLocation();
  // Route is a static path — use useRouteMatch to extract the product segment.
  const match = useRouteMatch<{ product: string }>('/activate/:product/wellness');
  const product = (match?.params.product ?? 'sleepio-canopy') as Product;
  const posthog = usePostHog();
  const lastRequestId = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [queryError, setQueryError] = useState(false);

  const [searchOrganizations, { loading: searchLoading }] = useLazyQuery(
    ORGANIZATIONS_BY_PREFIX_QUERY,
  );

  useConsent();

  // Gated on the flag, not just mount: while it's still resolving (null) or false, this
  // screen redirects away below without the user ever really landing on it.
  // org_slug isn't known yet at this entry point — EmployerSelected carries it once resolved.
  useEffect(() => {
    if (isSupabaseAuthEnabled) {
      posthog?.capture('ActivationFlowStarted', { program: SLEEPIO_CANOPY_PROGRAM });
    }
  }, [posthog, isSupabaseAuthEnabled]);

  // useEffect must be called unconditionally before any early returns (Rules of Hooks).
  // Guard the search logic internally on the flag being ready.
  useEffect(() => {
    if (!isSupabaseAuthEnabled) { return undefined; }

    const timeoutId = setTimeout(() => {
      if (selectedOrg || searchQuery.length < MIN_QUERY_LENGTH) {
        setOrganizations([]);
        setShowSuggestions(false);
        return;
      }

      lastRequestId.current += 1;
      const requestId = lastRequestId.current;

      searchOrganizations({
        variables: {
          prefix: searchQuery,
          limit: DROPDOWN_RESULTS_LIMIT,
          searchFilters: JSON.stringify({ organization_type: 'emp' }),
        },
        onCompleted: (data) => {
          if (requestId !== lastRequestId.current) { return; }
          setOrganizations(data?.organizationsByPrefix || []);
          setShowSuggestions(true);
          setQueryError(false);
        },
        onError: (err) => {
          if (requestId !== lastRequestId.current) { return; }
          logError('[EmployerSelection]', 'organizationsByPrefix query failed', err);
          posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
          setQueryError(true);
          setOrganizations([]);
          setShowSuggestions(true);
        },
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedOrg, searchOrganizations, isSupabaseAuthEnabled]);

  if (isSupabaseAuthEnabled === null) {
    return (
      <ThemeProvider theme={getTheme(product)}>
        <ContentContainer>
          <SleepioHeader />
          <Container
            maxWidth="xs"
            style={{
              flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center',
            }}
          >
            <CircularProgress color="primary" />
          </Container>
          <Footer />
        </ContentContainer>
      </ThemeProvider>
    );
  }
  if (!isSupabaseAuthEnabled) {
    return <Redirect to="/activate/sleepio/employers" />;
  }

  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    setQueryError(false);
    if (selectedOrg && value !== (selectedOrg.name ?? selectedOrg.slug)) {
      setSelectedOrg(null);
    }
  };

  const handleOrganizationSelect = (org: Organization) => {
    setSelectedOrg(org);
    setSearchQuery(org.name ?? org.slug);
    setShowSuggestions(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) { return; }

    // This screen is the first hop of sign-up and is routed outside ActivationRouter
    // (src/index.tsx), so a redirectTo dropped here is dropped for the whole flow —
    // ActivationRouter never sees it, so it never reaches session storage either.
    const params = new URLSearchParams(location.search);
    const redirectParam = buildRedirectParam(params.get('redirectTo'));

    posthog?.capture('EmployerSelected', { program: SLEEPIO_CANOPY_PROGRAM, org_slug: selectedOrg.slug });
    history.push(`/activate/${product}/${SLEEPIO_CANOPY_LOB}/${selectedOrg.slug}/sign-up${redirectParam}`);
  };

  return (
    <ThemeProvider theme={getTheme(product)}>
      <ContentContainer>
        <SleepioHeader />
        <Container maxWidth="xs" style={{ flex: 1 }}>
          <Typography variant="h2" style={{ marginTop: 32 }} color="secondary">
            Find your employer
          </Typography>
          <Typography style={{ marginTop: 8, marginBottom: 32 }} color="secondary">
            Search for your employer to get started.
          </Typography>
          <form onSubmit={handleSubmit}>
            <InputContainer>
              <TextField
                id="employer-name-input"
                autoFocus
                required
                label="Employer name"
                value={searchQuery}
                onChange={(e) => handleInputChange(e.target.value)}
                inputProps={{ placeholder: 'Start typing your employer…', 'aria-label': 'Employer name' }}
              />
              {showSuggestions && (
                <SuggestionsContainer elevation={3}>
                  <SuggestionContent
                    loading={searchLoading}
                    error={queryError}
                    organizations={organizations}
                    onSelect={handleOrganizationSelect}
                  />
                </SuggestionsContainer>
              )}
            </InputContainer>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              color="primary"
              disabled={!selectedOrg}
              rounded
            >
              Continue
            </Button>
          </form>
        </Container>
        <Footer />
      </ContentContainer>
    </ThemeProvider>
  );
};

export default EmployerSelection;
